import { useState, useEffect } from 'react';
import { resourceRegistry } from '../services/resource-registry';
import { useAppDataContext } from '../framework/app/app.data.context';

interface SchemaReference {
  schemaId: string;
  domain: string;
  category: string;
  required: boolean;
  description: string;
}

interface DomainConfiguration {
  id: string;
  name: string;
  description: string;
  schemaReferences: SchemaReference[];
  tags: string[];
}

interface Schema {
  id: string;
  _id?: string;
  name: string;
  key: string;
  domain: string;
  description: string;
  editorSchema?: string; // JSON Schema for entity editor
  jsonSchema?: string; // Full entity JSON Schema
}

interface DataModel {
  id: string;
  name: string;
  key: string;
  scope: string;
  schemas: Schema[];
  configuration: {
    domains: DomainConfiguration[];
  };
}

export interface DomainWithSchemas {
  id: string;
  name: string;
  description: string;
  schemas: Schema[];
}

export const useDataModelHierarchy = () => {
  const [domains, setDomains] = useState<DomainWithSchemas[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const context = useAppDataContext();

  useEffect(() => {
    if (context?.apiUrl && context?.appContext?.tokenProvider) {
      loadHierarchy();
    }
  }, [context?.apiUrl]);

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 useDataModelHierarchy: Fetching DataModels...');
      
      const apiUrl = context?.apiUrl || 'http://localhost:5002';
      const token = await context?.appContext?.tokenProvider?.getIdToken();
      
      // Fetch active data models directly (not via dataProvider to avoid QueryClient dependency)
      const response = await fetch(`${apiUrl}/datamodel-dp?filter=${encodeURIComponent(JSON.stringify({ 'configuration.isActive': true }))}&range=[0,99]&sort=["scope","ASC"]`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch DataModels: ${response.statusText}`);
      }
      
      const responseData = await response.json();
      console.log('📦 useDataModelHierarchy: Raw response:', { 
        type: typeof responseData, 
        isArray: Array.isArray(responseData),
        keys: responseData ? Object.keys(responseData) : [],
        hasResult: !!responseData?.result
      });
      
      // Backend returns {result: [...], paginationResponse: {...}}
      const dataModels = responseData?.result || [];
      console.log(`✅ useDataModelHierarchy: Fetched ${dataModels.length} DataModels`);
      
      // Simplified: Group schemas by their domain field (just a string!)
      // No more complex schemaReferences tracking
      const domainMap = new Map<string, DomainWithSchemas>();
      
      dataModels.forEach(dataModel => {
        dataModel.schemas?.forEach((schema: any) => {
          // Get domain from schema's domain field (defaulting to "General" if not set)
          const domainName = schema.domain || 'General';
          
          // Get or create domain
          const existingDomain = domainMap.get(domainName) || {
            id: domainName.toLowerCase(),
            name: domainName,
            description: `${domainName} domain entities`,
            schemas: []
          };
          
          // Add schema to domain (avoid duplicates)
          if (!existingDomain.schemas.find(s => s.key === schema.key)) {
            // Log schema details for debugging
            console.log(`📋 Adding schema to domain "${domainName}":`, {
              key: schema.key,
              name: schema.name,
              hasName: !!schema.name,
              domain: schema.domain,
              schemaObjectKeys: Object.keys(schema)
            });
            
            existingDomain.schemas.push(schema);
            
            // Register this resource with its API path and schema info
            // Path format: /data-models/{dataModelKey}/{schemaKey}
            resourceRegistry.register(schema.key, {
              resourceName: schema.key,
              apiPath: `data-models/${dataModel.key}/${schema.key}`,
              dataModelKey: dataModel.key,
              schemaKey: schema.key,
              schemaId: schema.id || schema._id
            });
          }
          
          domainMap.set(domainName, existingDomain);
        });
      });
      
      // Convert to array and sort
      const domainsArray = Array.from(domainMap.values())
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setDomains(domainsArray);
    } catch (err) {
      console.error('Failed to load data model hierarchy:', err);
      setError(err instanceof Error ? err.message : 'Failed to load hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const refreshHierarchy = async () => {
    await loadHierarchy();
  };

  return {
    domains,
    loading,
    error,
    refreshHierarchy
  };
};

